import React, { useEffect, useState } from 'react';
import { createOrder } from '../services/orders';
import { sendOrderSmsVerification } from '../services/sms';
import PaymentModal from './PaymentModal';

const OrderModal = ({ product, cartItems = [], onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    upazila: '',
    district: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successCountdownMs, setSuccessCountdownMs] = useState(0);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const parsePriceToInt = (p) => {
    if (typeof p === 'number') return p;
    if (!p) return 0;
    const bnToEn = { '০':'0','১':'1','২':'2','৩':'3','৪':'4','৫':'5','৬':'6','৭':'7','৮':'8','৯':'9' };
    const norm = String(p).replace(/[০-৯]/g, (d) => bnToEn[d] || d).replace(/[^0-9]/g, '');
    return parseInt(norm || '0', 10);
  };

  const parseProductId = (id) => {
    if (typeof id === 'string' && id.length > 0) return id;
    if (typeof id === 'number') return id.toString();
    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowPayment(true);
  };

  const handlePaymentSubmit = async (paymentData) => {
    setIsSubmitting(true);
    try {
      const verificationCode = Math.floor(100000 + Math.random() * 900000).toString();
      let items = [];

      if (cartItems && cartItems.length > 0) {
        items = cartItems.map((it) => ({
          product_id: parseProductId(it.product_id),
          quantity: it.quantity || 1,
          unit_price: parsePriceToInt(it.unit_price),
        })).filter((it) => !!it.product_id);
      } else if (product) {
        const finalPrice = product.offer_price || product.price;
        const productId = parseProductId(product.id);
        if (productId) {
          items = [{ product_id: productId, quantity: 1, unit_price: parsePriceToInt(finalPrice) }];
        }
      }

      const order = await createOrder({
        items,
        customer: {
          name: formData.name,
          phone: formData.phone,
          address: formData.address,
          upazila: formData.upazila,
          district: formData.district
        },
        notes: cartItems && cartItems.length > 0
          ? `Cart: ${cartItems.map((it) => `${it.name_bn} x${it.quantity}`).join(', ')}`
          : `Product: ${product.name_bn} | Price: ৳${product.offer_price || product.price}${product?.offer_price ? ` (Offer from ৳${product.price})` : ''}`,
        verificationCode,
        payment_method: paymentData.payment_method,
        payment_transaction_id: paymentData.transaction_id || null,
        payment_sender_number: paymentData.sender_number || null,
        // Store values from PaymentModal - they're already calculated as decimals
        // PaymentModal sends: deliveryCharge (50.00), transactionFee (4.62), totalAmount (303.62)
        // Store as integers * 100 to preserve decimals: 50.00 -> 5000, 4.62 -> 462, 303.62 -> 30362
        delivery_charge: Math.round((paymentData.delivery_charge || 0) * 100),
        transaction_fee: Math.round((paymentData.transaction_fee || 0) * 100),
        total_amount: Math.round((paymentData.total_amount || 0) * 100)
      });

      console.log('Order created successfully:', order);

      // Fire SMS verification (placeholder; will be wired to real API later)
      try {
        await sendOrderSmsVerification({ phone: formData.phone, orderId: order.id, code: verificationCode });
      } catch (smsError) {
        console.error('SMS sending failed:', smsError);
        // Don't block order success if SMS fails
      }

      setShowPayment(false);
      setIsSuccess(true);
      setSuccessCountdownMs(12000);
    } catch (error) {
      console.error('Error submitting order:', error);
      const errorMessage = error.message || 'Unknown error occurred';
      alert(`অর্ডার জমা দেওয়ার সময় সমস্যা হয়েছে: ${errorMessage}. দয়া করে আবার চেষ্টা করুন।`);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isSuccess) return;
    const interval = setInterval(() => {
      setSuccessCountdownMs((prev) => Math.max(0, prev - 100));
    }, 100);
    const timeout = setTimeout(() => {
      onClose();
    }, 12000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isSuccess, onClose]);

  if (isSuccess) {
    const progressPercent = ((12000 - successCountdownMs) / 12000) * 100;
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
        <div className="bg-white rounded-2xl p-6 max-w-md w-full text-center relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-slate-500 hover:text-slate-700 p-2 rounded-lg hover:bg-slate-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2 bengali-text">
            ধন্যবাদ!
          </h3>
          <p className="text-lg text-gray-600 mb-2 bengali-text">
            আপনার অর্ডারটি সফলভাবে গ্রহণ করা হয়েছে!
          </p>
          <p className="text-sm text-gray-500 bengali-text mb-4">
            আমরা শীঘ্রই আপনার সাথে যোগাযোগ করব।
          </p>
          <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-green-500 transition-all" style={{ width: `${progressPercent}%` }} />
          </div>
        </div>
      </div>
    );
  }

  // Show payment modal if user clicked proceed
  if (showPayment) {
    const finalPrice = cartItems && cartItems.length > 0
      ? cartItems.reduce((sum, it) => sum + (parseFloat(it.unit_price || 0) * (it.quantity || 1)), 0)
      : (product.offer_price || product.price);
    return (
      <PaymentModal
        orderAmount={finalPrice}
        onPaymentSubmit={handlePaymentSubmit}
        onClose={() => setShowPayment(false)}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4 modal-backdrop">
      <div 
        className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 bengali-text">
            অর্ডার করুন
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Product/Cart Info */}
        {(cartItems && cartItems.length > 0) ? (
          <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-3">
            <h3 className="font-semibold text-gray-900 bengali-text">ঝুড়ির পণ্যসমূহ</h3>
            {cartItems.map((it) => (
              <div key={it.product_id} className="flex justify-between text-sm">
                <span className="text-gray-700 truncate bengali-text">{it.name_bn} × {it.quantity}</span>
                <span className="font-medium">৳{(parseFloat(it.unit_price || 0) * (it.quantity || 1)).toFixed(2)}</span>
              </div>
            ))}
            <div className="border-t pt-2 flex justify-between">
              <span className="text-sm text-gray-700 bengali-text">সাবটোটাল</span>
              <span className="text-lg font-bold text-orange-600">৳{cartItems.reduce((s, it) => s + (parseFloat(it.unit_price || 0) * (it.quantity || 1)), 0).toFixed(2)}</span>
            </div>
          </div>
        ) : (
          product ? (
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="flex items-center space-x-4">
                <img
                  src={product.image}
                  alt={product.name_bn}
                  className="w-16 h-16 object-cover rounded-lg"
                />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 bengali-text">
                    {product.name_bn}
                  </h3>
                  {product.offer_price ? (
                    <div>
                      <p className="text-orange-custom font-bold text-lg bengali-text">
                        ৳{product.offer_price}
                      </p>
                      <p className="text-gray-400 line-through text-sm bengali-text">
                        ৳{product.price}
                      </p>
                    </div>
                  ) : (
                    <p className="text-orange-custom font-bold text-lg bengali-text">
                      ৳{product.price}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : null
        )}

        {/* Order Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 bengali-text">
              নাম *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              autoFocus
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-custom focus:border-transparent bengali-text"
              placeholder="আপনার পূর্ণ নাম লিখুন"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 bengali-text">
              মোবাইল নাম্বার *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-custom focus:border-transparent"
              placeholder="০১XXXXXXXXX"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 bengali-text">
              পূর্ণ ঠিকানা *
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-custom focus:border-transparent bengali-text"
              placeholder="বিস্তারিত ঠিকানা লিখুন"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 bengali-text">
                থানা *
              </label>
              <input
                type="text"
                name="upazila"
                value={formData.upazila}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-custom focus:border-transparent bengali-text"
                placeholder="থানার নাম"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 bengali-text">
                জেলা *
              </label>
              <input
                type="text"
                name="district"
                value={formData.district}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-custom focus:border-transparent bengali-text"
                placeholder="জেলার নাম"
              />
            </div>
          </div>

          {/* Payment Info */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <p className="text-sm text-orange-800 font-semibold bengali-text">
              পরবর্তী ধাপে পেমেন্ট নিশ্চিত করুন
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors bengali-text"
            >
              বাতিল
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-orange-custom hover:bg-orange-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed bengali-text"
            >
              {isSubmitting ? 'জমা দেওয়া হচ্ছে...' : 'অর্ডার নিশ্চিত করুন'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrderModal;
